class ChatWebSocketService {
  constructor() {
    // Singleton pattern
    if (ChatWebSocketService._instance) {
      return ChatWebSocketService._instance;
    }
    ChatWebSocketService._instance = this;

    // Core configuration
    this.config = {
      baseUrl: null,
      orgId: null,
      participantId: null,
      connectionType: null, // 'global' or 'chat'
      chatId: null,
    };

    // Connection state
    this.state = {
      isConnected: false,
      isConnecting: false,
      shouldReconnect: true,
      reconnectAttempts: 0,
    };

    // Message handlers and configuration
    this.messageHandlers = new Map();
    this.RECONNECT_CONFIG = {
      MAX_ATTEMPTS: 3,
      BASE_DELAY: 1000,
      TIMEOUT: 10000,
    };

    this.socket = null;
  }

  // Initialize the service
  init({ baseUrl, orgId, participantId }) {
    this.config.baseUrl = baseUrl;
    this.config.orgId = orgId;
    this.config.participantId = participantId;
    return this;
  }

  // Connect to global channel
  async connectGlobal() {
    if (this.state.isConnected && this.config.connectionType === 'global') {
      // console.log('Already connected to global channel');
      return Promise.resolve();
    }

    this.config.connectionType = 'global';
    this.config.chatId = null;

    return this._connect();
  }

  // Connect to specific chat channel
  async connectChat(chatId) {
    if (!chatId) {
      throw new Error('chatId is required for chat connection');
    }

    // If we're already connecting to the same chat, return a resolved promise
    if (this.state.isConnecting && this.config.chatId === chatId) {
      // console.log(
      //   'Already connecting to this chat, ignoring duplicate request',
      // );
      return Promise.resolve();
    }

    if (
      this.state.isConnected &&
      this.config.connectionType === 'chat' &&
      this.config.chatId === chatId
    ) {
      // console.log(`Already connected to chat ${chatId}`);
      return Promise.resolve();
    }

    // Disconnect from existing connection before switching
    if (this.state.isConnected) {
      // console.log('disconnecting from existing connection');
      await this.disconnect('Switching chats');
    }

    this.config.connectionType = 'chat';
    this.config.chatId = chatId;

    return this._connect();
  }

  // Disconnect from the WebSocket
  async disconnect(reason = 'Client disconnect') {
    // console.log('disconnecting from websocket');
    if (this.socket) {
      this.state.isConnecting = false;
      this.state.shouldReconnect = false;

      return new Promise((resolve) => {
        // Handle the case where socket is already closed
        if (this.socket.readyState === WebSocket.CLOSED) {
          this.socket = null;
          this.state.isConnected = false;
          resolve();
          return;
        }

        // Add one-time close listener to resolve the promise
        const onClose = () => {
          this.socket = null;
          this.state.isConnected = false;
          resolve();
        };
        this.socket.addEventListener('close', onClose, { once: true });

        // Initiate close
        this.socket.close(1000, reason);
      });
    }
    return Promise.resolve();
  }

  // Register a message handler
  onMessage(type, callback) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    this.messageHandlers.get(type).add(callback);
    return () => this.messageHandlers.get(type).delete(callback);
  }

  // Private methods
  async _connect() {
    if (this.state.isConnecting) {
      throw new Error('Connection already in progress');
    }

    if (
      !this.config.baseUrl ||
      !this.config.orgId ||
      !this.config.participantId
    ) {
      throw new Error('Missing required configuration');
    }

    this.state.isConnecting = true;
    this.state.shouldReconnect = true;

    return this._establishConnection();
  }

  async _establishConnection() {
    return new Promise((resolve, reject) => {
      let timeoutId;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      try {
        const url = this._buildUrl();
        // console.log('connecting to', url);
        this.socket = new WebSocket(url);

        // Set up timeout that only triggers if we haven't connected or failed yet
        timeoutId = setTimeout(() => {
          if (!this.state.isConnected) {
            this.state.isConnecting = false;
            this.socket?.close();
            reject(new Error('Connection timeout'));
          }
        }, this.RECONNECT_CONFIG.TIMEOUT);

        this.socket.onopen = () => {
          cleanup();
          this.state.isConnected = true;
          this.state.isConnecting = false;
          this.state.reconnectAttempts = 0;
          resolve();
        };

        this.socket.onclose = (event) => {
          // console.log('websocket closed', event);
          cleanup();
          this._handleClose(event);
          // Only reject if we haven't already connected successfully
          if (!this.state.isConnected) {
            reject(
              new Error(
                `Connection closed: ${event.code} ${event.reason || ''}`,
              ),
            );
          }
        };

        this.socket.onerror = (event) => {
          cleanup();
          this._handleError(event);
          // Only reject if we haven't already connected successfully
          if (!this.state.isConnected) {
            reject(new Error('WebSocket connection failed'));
          }
        };

        this.socket.onmessage = this._handleMessage.bind(this);
      } catch (error) {
        cleanup();
        this.state.isConnecting = false;
        reject(error);
      }
    });
  }

  _buildUrl() {
    const { baseUrl, connectionType, participantId, orgId, chatId } =
      this.config;
    let url = `${baseUrl}?connectionType=${connectionType}&participantId=${participantId}&orgId=${orgId}`;
    return chatId ? `${url}&chatId=${chatId}` : url;
  }

  _handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      if (message.type && this.messageHandlers.has(message.type)) {
        this.messageHandlers
          .get(message.type)
          .forEach((handler) => handler(message));
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
    }
  }

  _handleClose(event) {
    this.state.isConnected = false;
    this.state.isConnecting = false;
    this.socket = null;

    if (this.state.shouldReconnect && event.code !== 1000) {
      // console.log('attempting reconnect');
      this._attemptReconnect();
    }
  }

  _handleError(event) {
    console.error('WebSocket error:', event);
  }

  _attemptReconnect() {
    if (this.state.reconnectAttempts >= this.RECONNECT_CONFIG.MAX_ATTEMPTS) {
      this.state.shouldReconnect = false;
      return;
    }

    this.state.reconnectAttempts++;
    const delay =
      this.RECONNECT_CONFIG.BASE_DELAY *
      Math.pow(2, this.state.reconnectAttempts - 1);

    setTimeout(() => {
      if (
        this.state.shouldReconnect &&
        !this.state.isConnecting &&
        !this.state.isConnected
      ) {
        this._establishConnection().catch((error) => {
          console.error('Reconnection failed:', error);
        });
      }
    }, delay);
  }
}

const chatWebSocketService = new ChatWebSocketService();
export default chatWebSocketService;
